import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/client';
import { useAuth } from '../context/AuthContext';

interface Post {
  id: number;
  title: string;
  content: string;
  createdAt: string;
  authorUsername: string;
  replyCount: number;
}

interface Reply {
  id: number;
  content: string;
  createdAt: string;
  authorUsername: string;
}

export default function ForumPage() {
  const [posts, setPosts] = useState<Post[]>([]);
  const [selectedPost, setSelectedPost] = useState<Post | null>(null);
  const [replies, setReplies] = useState<Reply[]>([]);
  const [loading, setLoading] = useState(true);
  const [showNewPost, setShowNewPost] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newContent, setNewContent] = useState('');
  const [replyContent, setReplyContent] = useState('');
  const [formError, setFormError] = useState('');
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    try {
      const res = await api.get('/api/forum');
      setPosts(res.data);
    } finally {
      setLoading(false);
    }
  };

  const openPost = async (post: Post) => {
    setSelectedPost(post);
    const res = await api.get(`/api/forum/${post.id}/replies`);
    setReplies(res.data);
  };

  const submitPost = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');
    try {
      await api.post('/api/forum', { title: newTitle, content: newContent });
      setNewTitle('');
      setNewContent('');
      setShowNewPost(false);
      fetchPosts();
    } catch (err: any) {
      setFormError(err.response?.data?.message || 'Fejl ved oprettelse.');
    }
  };

  const submitReply = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedPost) return;
    if (!user) { navigate('/login'); return; }
    try {
      await api.post(`/api/forum/${selectedPost.id}/replies`, { content: replyContent });
      setReplyContent('');
      const res = await api.get(`/api/forum/${selectedPost.id}/replies`);
      setReplies(res.data);
      // Update reply count in list
      setPosts((prev) => prev.map((p) => p.id === selectedPost.id ? { ...p, replyCount: res.data.length } : p));
    } catch { /* ignore */ }
  };

  const formatDate = (dateStr: string) =>
    new Date(dateStr).toLocaleDateString('da-DK', { day: 'numeric', month: 'long', year: 'numeric' });

  if (loading) return <div className="page-loading">Indlæser forum... 💬</div>;

  if (selectedPost) {
    return (
      <div className="page-container">
        <div className="page-header">
          <button className="btn btn-outline" onClick={() => setSelectedPost(null)}>
            ← Tilbage til forum
          </button>
        </div>

        <div className="card forum-post-detail">
          <div className="post-meta">
            <span className="post-author">👤 {selectedPost.authorUsername}</span>
            <span className="post-date">{formatDate(selectedPost.createdAt)}</span>
          </div>
          <h1 className="post-title-large">{selectedPost.title}</h1>
          <p className="post-content-full">{selectedPost.content}</p>
        </div>

        <div className="replies-section">
          <h2>💬 Svar ({replies.length})</h2>
          {replies.map((reply) => (
            <div key={reply.id} className="card reply-card">
              <div className="reply-header">
                <span className="post-author">👤 {reply.authorUsername}</span>
                <span className="post-date">{formatDate(reply.createdAt)}</span>
              </div>
              <p className="reply-content">{reply.content}</p>
            </div>
          ))}

          {user ? (
            <form className="card reply-form" onSubmit={submitReply}>
              <h3>Skriv et svar</h3>
              <textarea
                className="form-input"
                value={replyContent}
                onChange={(e) => setReplyContent(e.target.value)}
                placeholder="Dit svar..."
                rows={3}
                required
              />
              <button type="submit" className="btn btn-primary">Send svar</button>
            </form>
          ) : (
            <div className="card reply-form">
              <p>
                <a href="/login" className="auth-link">Log ind</a> for at svare.
              </p>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>💬 Forum</h1>
        {user && (
          <button className="btn btn-primary" onClick={() => setShowNewPost(!showNewPost)}>
            {showNewPost ? '✕ Annuller' : '+ Nyt indlæg'}
          </button>
        )}
      </div>

      {showNewPost && (
        <form className="card form-card" onSubmit={submitPost}>
          <h2>Nyt indlæg</h2>
          <div className="form-group">
            <label>Titel</label>
            <input className="form-input" value={newTitle} onChange={(e) => setNewTitle(e.target.value)} required placeholder="Titel på dit indlæg..." />
          </div>
          <div className="form-group">
            <label>Indhold</label>
            <textarea className="form-input" value={newContent} onChange={(e) => setNewContent(e.target.value)} required placeholder="Hvad vil du skrive om?" rows={4} />
          </div>
          {formError && <div className="alert alert-error">{formError}</div>}
          <button type="submit" className="btn btn-primary">Opret indlæg</button>
        </form>
      )}

      {posts.length === 0 ? (
        <div className="empty-state">
          <span>🤫</span>
          <p>Ingen indlæg endnu. Vær den første til at starte en diskussion!</p>
        </div>
      ) : (
        <div className="forum-list">
          {posts.map((post) => (
            <div key={post.id} className="card forum-post-card" onClick={() => openPost(post)}>
              <h2 className="forum-post-title">{post.title}</h2>
              <p className="forum-post-excerpt">{post.content.slice(0, 150)}{post.content.length > 150 ? '...' : ''}</p>
              <div className="forum-post-meta">
                <span>👤 {post.authorUsername}</span>
                <span>📅 {formatDate(post.createdAt)}</span>
                <span>💬 {post.replyCount} svar</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
